import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { Shield, Plus, Edit, Trash2, Loader2, Check, X, Users, Lock, LockOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function RolesManagement() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionGroups, setPermissionGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${API}/rbac/roles`),
        axios.get(`${API}/rbac/permissions`)
      ]);
      setRoles(rolesRes.data.roles || []);
      setPermissions(permsRes.data.permissions || {});
      setPermissionGroups(permsRes.data.groups || {});
    } catch (error) {
      toast.error('Failed to load roles data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateDialog = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: [] });
    setDialogOpen(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: [...(role.permissions || [])]
    });
    setDialogOpen(true);
  };

  const handlePermissionToggle = (permission) => {
    setFormData(prev => {
      const newPerms = prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission];
      return { ...prev, permissions: newPerms };
    });
  };

  const handleGroupToggle = (groupPerms, checked) => {
    setFormData(prev => {
      let newPerms = [...prev.permissions];
      if (checked) {
        // Add all permissions in group
        groupPerms.forEach(p => {
          if (!newPerms.includes(p)) newPerms.push(p);
        });
      } else {
        // Remove all permissions in group
        newPerms = newPerms.filter(p => !groupPerms.includes(p));
      }
      return { ...prev, permissions: newPerms };
    });
  };

  const isGroupChecked = (groupPerms) => {
    return groupPerms.every(p => formData.permissions.includes(p));
  };

  const isGroupIndeterminate = (groupPerms) => {
    const checkedCount = groupPerms.filter(p => formData.permissions.includes(p)).length;
    return checkedCount > 0 && checkedCount < groupPerms.length;
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingRole) {
        await axios.put(`${API}/rbac/roles/${editingRole.id}`, formData);
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API}/rbac/roles`, formData);
        toast.success('Role created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/rbac/roles/${roleToDelete.id}`);
      toast.success('Role deleted successfully');
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Roles & Permissions</h3>
          <p className="text-sm text-slate-500">Manage access control roles and their permissions</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-role-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Custom Role
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {roles.map(role => (
          <Card key={role.id} className={`${role.is_system ? 'border-indigo-200 bg-indigo-50/30' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {role.is_system ? (
                      <Lock className="h-4 w-4 text-indigo-600" />
                    ) : (
                      <LockOpen className="h-4 w-4 text-slate-400" />
                    )}
                    {role.name}
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">{role.description}</CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(role)} title="Edit Role">
                    <Edit className="h-4 w-4" />
                  </Button>
                  {!role.is_system && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => confirmDelete(role)} title="Delete Role">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Shield className="h-4 w-4" />
                  <span>{role.permissions?.length || 0} permissions</span>
                </div>
                {role.is_system && (
                  <Badge variant="outline" className="text-xs bg-indigo-100 text-indigo-700 border-indigo-200">
                    System Role
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Sales Manager"
                  disabled={editingRole?.is_system}
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this role"
                  data-testid="role-description-input"
                />
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              <Label className="mb-2 block">Permissions ({formData.permissions.length} selected)</Label>
              <ScrollArea className="h-[400px] border rounded-lg p-4">
                <Accordion type="multiple" className="w-full">
                  {Object.entries(permissionGroups).map(([groupName, groupPerms]) => (
                    <AccordionItem key={groupName} value={groupName}>
                      <AccordionTrigger className="hover:no-underline py-2">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isGroupChecked(groupPerms)}
                            ref={(el) => {
                              if (el) el.indeterminate = isGroupIndeterminate(groupPerms);
                            }}
                            onCheckedChange={(checked) => handleGroupToggle(groupPerms, checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="font-medium text-sm">{groupName}</span>
                          <Badge variant="outline" className="text-xs">
                            {groupPerms.filter(p => formData.permissions.includes(p)).length}/{groupPerms.length}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-4">
                        <div className="grid grid-cols-1 gap-2 pl-8">
                          {groupPerms.map(perm => (
                            <label key={perm} className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors">
                              <Checkbox
                                checked={formData.permissions.includes(perm)}
                                onCheckedChange={() => handlePermissionToggle(perm)}
                              />
                              <div className="flex-1">
                                <span className="text-sm font-medium">{permissions[perm]}</span>
                                <span className="text-xs text-slate-400 ml-2">({perm})</span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-role-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete the role "<strong>{roleToDelete?.name}</strong>"? 
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
